import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  createProjectDocumentRevisionFiles,
  deleteProjectDocumentRevisionFile,
  getProjectDocumentRevisionFile,
  ProjectDocumentRevisionFile as DocumentRevisionFileForSubmit,
  reUploadProjectDocumentRevisionFile,
  updateProjectDocumentRevisionFile
} from '../../api/project-document-revision-files';
import { getProjectDocumentRevisionAsync } from '../ProjectDocumentRevision/projectDocumentRevisionSlice';
import { ProjectDocumentVersionFile } from '../ProjectDocumentVersionFile/projectDocumentVersionFileSlice';

export const getProjectDocumentRevisionFileAsync = createAsyncThunk(
  'projectDocumentRevisionFile/getProjectDocumentRevisionFile',
  async (projectDocumentRevisionFileId: number, thunkAPI) => {
    const projectDocumentRevisionFile = await getProjectDocumentRevisionFile(
      projectDocumentRevisionFileId
    );
    thunkAPI.dispatch(
      getProjectDocumentRevisionAsync(
        projectDocumentRevisionFile.projectDocumentRevisionId
      )
    );
    return projectDocumentRevisionFile;
  }
);

export const createProjectDocumentRevisionFilesAsync = createAsyncThunk(
  'projectDocumentRevisionFiles/createProjectDocumentRevisionFiles',
  async (
    {
      projectDocumentRevisionFiles,
      projectDocumentRevisionId
    }: {
      projectDocumentRevisionFiles: DocumentRevisionFileForSubmit[];
      projectDocumentRevisionId: number;
    },
    thunkAPI
  ) => {
    const projectDocumentRevisionFileResults =
      await createProjectDocumentRevisionFiles({
        projectDocumentRevisionFiles,
        projectDocumentRevisionId
      });
    thunkAPI.dispatch(
      getProjectDocumentRevisionAsync(projectDocumentRevisionId)
    );
    return projectDocumentRevisionFileResults;
  }
);

export const reUploadProjectDocumentRevisionFileAsync = createAsyncThunk(
  'projectDocumentRevisionFiles/reUploadProjectDocumentRevisionFile',
  async (
    {
      projectDocumentRevisionFile,
      projectDocumentRevisionFileId,
      projectDocumentRevisionId
    }: {
      projectDocumentRevisionFile: DocumentRevisionFileForSubmit;
      projectDocumentRevisionFileId: number;
      projectDocumentRevisionId: number;
    },
    thunkAPI
  ) => {
    const projectDocumentRevisionFileResult =
      await reUploadProjectDocumentRevisionFile({
        projectDocumentRevisionFile,
        projectDocumentRevisionFileId
      });
    thunkAPI.dispatch(
      getProjectDocumentRevisionAsync(projectDocumentRevisionId)
    );
    return projectDocumentRevisionFileResult;
  }
);

export const updateProjectDocumentRevisionFileAsync = createAsyncThunk(
  'projectDocumentRevisionFiles/updateProjectDocumentRevisionFile',
  async (
    {
      isPublic,
      projectDocumentRevisionFileId,
      projectDocumentRevisionId
    }: {
      isPublic: boolean;
      projectDocumentRevisionFileId: number;
      projectDocumentRevisionId: number;
    },
    thunkAPI
  ) => {
    await updateProjectDocumentRevisionFile({
      isPublic,
      projectDocumentRevisionFileId
    });
    thunkAPI.dispatch(
      getProjectDocumentRevisionAsync(projectDocumentRevisionId)
    );
  }
);

export const deleteProjectDocumentRevisionFileAsync = createAsyncThunk(
  'projectDocumentRevisionFiles/deleteProjectDocumentRevisionFile',
  async (
    {
      projectDocumentRevisionFileId,
      projectDocumentRevisionId
    }: {
      projectDocumentRevisionFileId: number;
      projectDocumentRevisionId: number;
    },
    thunkAPI
  ) => {
    await deleteProjectDocumentRevisionFile(projectDocumentRevisionFileId);
    thunkAPI.dispatch(
      getProjectDocumentRevisionAsync(projectDocumentRevisionId)
    );
  }
);

export interface ProjectDocumentRevisionFile {
  id: number;
  name: string;
  description?: string | null;
  isPublic: boolean;
  s3ContentType: string;
  projectDocumentVersionFiles: ProjectDocumentVersionFile[];
}

interface ProjectDocumentRevisionFileState {
  selectedProjectDocumentRevisionFile: ProjectDocumentRevisionFile | null;
}

const initialState: ProjectDocumentRevisionFileState = {
  selectedProjectDocumentRevisionFile: null
};

export default createSlice({
  name: 'projectDocumentRevisionFiles',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      getProjectDocumentRevisionFileAsync.fulfilled,
      (state, action) => {
        state.selectedProjectDocumentRevisionFile = action.payload;
      }
    );
  }
});
